/**
 * Auto-advances the product media gallery.
 *
 * Images advance on a fixed interval. Video plays through to its end before the
 * gallery moves on, so a clip is never cut off part way. Rotation only runs
 * while the gallery is on screen, which also stops setActiveMedia from
 * scrolling the page back up while the visitor is reading further down.
 */
(() => {
  const INITIALISED = new WeakSet();
  const MIN_INTERVAL = 2000;

  class GalleryAutoplay {
    constructor(gallery) {
      this.gallery = gallery;
      const seconds = parseFloat(gallery.dataset.snAutoplayInterval);
      this.interval = Math.max(MIN_INTERVAL, (seconds || 5) * 1000);

      this.timer = null;
      this.video = null;
      this.onVideoEnded = null;
      this.hovered = false;
      this.onScreen = false;

      this.bind();
      this.watchVisibility();
    }

    bind() {
      const hold = () => {
        this.hovered = true;
        this.stopTimer();
      };
      const release = () => {
        this.hovered = false;
        this.resume();
      };

      this.gallery.addEventListener('pointerenter', hold);
      this.gallery.addEventListener('pointerleave', release);
      this.gallery.addEventListener('focusin', hold);
      this.gallery.addEventListener('focusout', release);

      // A manual pick restarts the cycle from the newly chosen slide.
      this.gallery.addEventListener('click', () => {
        window.setTimeout(() => this.resume(), 50);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopTimer();
        else this.resume();
      });
    }

    watchVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.onScreen = true;
        this.resume();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.onScreen = entry.isIntersecting;
            if (this.onScreen) this.resume();
            else this.stopTimer();
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(this.gallery);
    }

    get viewer() {
      return this.gallery.querySelector('[id^="GalleryViewer"]');
    }

    /** Rotation order, skipping variant slides the theme keeps hidden. */
    get order() {
      const thumbnails = this.gallery.querySelector('[id^="GalleryThumbnails"]');
      if (thumbnails) {
        const visible = Array.from(thumbnails.querySelectorAll('[data-target]')).filter(
          (item) => item.offsetParent !== null
        );
        if (visible.length) return visible.map((item) => item.dataset.target);
      }

      const viewer = this.viewer;
      if (!viewer) return [];
      return Array.from(viewer.querySelectorAll('[data-media-id]')).map((item) => item.dataset.mediaId);
    }

    get activeItem() {
      const viewer = this.viewer;
      return viewer ? viewer.querySelector('[data-media-id].is-active') : null;
    }

    stopTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    releaseVideo() {
      if (this.video && this.onVideoEnded) {
        this.video.removeEventListener('ended', this.onVideoEnded);
        this.video.removeEventListener('error', this.onVideoEnded);
      }
      this.video = null;
      this.onVideoEnded = null;
    }

    /** Restarts scheduling for whatever slide is currently active. */
    resume() {
      this.stopTimer();
      this.releaseVideo();

      if (this.hovered || document.hidden || !this.onScreen) return;
      if (this.order.length < 2) return;

      const item = this.activeItem;
      if (!item) return;

      // Third-party players and 3D models cannot report when they finish,
      // so rotation waits for the visitor instead of cutting them off.
      if (item.querySelector('iframe, model-viewer, product-model')) return;

      const video = item.querySelector('video');
      if (video && !video.loop) {
        this.followVideo(video);
        return;
      }

      this.timer = window.setTimeout(() => this.advance(), this.interval);
    }

    followVideo(video) {
      this.video = video;
      this.onVideoEnded = () => this.advance();
      video.addEventListener('ended', this.onVideoEnded, { once: true });
      video.addEventListener('error', this.onVideoEnded, { once: true });

      video.muted = true;
      const started = video.play();
      if (started && typeof started.catch === 'function') {
        // Autoplay can be refused; fall back to the normal interval.
        started.catch(() => {
          this.releaseVideo();
          this.timer = window.setTimeout(() => this.advance(), this.interval);
        });
      }
    }

    advance() {
      this.stopTimer();
      this.releaseVideo();

      const order = this.order;
      if (order.length < 2) return;

      const item = this.activeItem;
      const current = item ? order.indexOf(item.dataset.mediaId) : -1;
      const next = order[(current + 1) % order.length];

      if (next && typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(next, false);
      }

      window.setTimeout(() => this.resume(), 50);
    }
  }

  function start(root = document) {
    root.querySelectorAll('media-gallery[data-sn-autoplay="true"]').forEach((gallery) => {
      if (INITIALISED.has(gallery)) return;
      INITIALISED.add(gallery);
      new GalleryAutoplay(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start());
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();
